// Used in page-header.component.ts, section-label.component.ts, tiles.component.ts, view-composite-assignment.component.ts, and view-assignment.component.ts

// Angular
import { Component, Inject } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialogRef,
} from "@angular/material/dialog";

// Services/Types/Helpers
import { AssetAssociationsHelper, DueDateStatusHelper } from "#core/helpers";
import { GetGradesHelper } from "#helpers/get-grades.helper";
import { LanguageHelper } from "#helpers/language.helper";
import {
    AssetTypeHelper,
    PPAssignment as Assignment,
    CompositeTaskAssignment,
} from "cli-types";

// Store/RXJS
import { AssignmentHelper } from "#helpers/assignment.helper";
import * as studentsStore from "#student/student-shared/store";
import { Asset, assetActivityTypeIds, AssetAssociation, assetTypeIds, Edition } from "@carnegie-learning/cure-types";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "assignment-info-dialog",
    templateUrl: "./assignment-info-dialog.component.html",
    styleUrls: ["./assignment-info-dialog.component.scss"],
    standalone: false
})
export class AssignmentInfoDialogComponent {
    private ngUnsubscribe = new Subject();
    readonly assetTypeIds = assetTypeIds;
    readonly assetActivityTypeIds = assetActivityTypeIds;

    constructor(
        private assignmentHelper: AssignmentHelper,
        public dialogRef: MatDialogRef<AssignmentInfoDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private assetTypeHelper: AssetTypeHelper,
        private assetAssociationsHelper: AssetAssociationsHelper,
        private getGradesHelper: GetGradesHelper,
        private dueDateStatusHelper: DueDateStatusHelper,
        private languageHelper: LanguageHelper,
        private courseStore: Store<studentsStore.StudentCoursesState>
    ) {
        // Make sure we have the subject data on Modal open
        this.courseStore
            .pipe(select(studentsStore.getAllActiveSubjects))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((subjects: AssetAssociation[]) => {
                if (subjects && subjects.length > 0) {
                    const subject = subjects.find(
                        (s) => s.subjectId === this.data.subjectId
                    );
                    if (subject) {
                        this.data.subject = subject;
                    }
                }
            });
    }

    getCompletionCount(tile: Assignment) {
        if (tile) {
            let count = 0;
            tile.compositeTaskAssignments.forEach((a) => {
                if (a.completedAt) {
                    count = count + 1;
                }
            });
            return count + "/" + tile.compositeTaskAssignments.length;
        }
    }

    getProperActivityWord(length: number) {
        if (length === 1) {
            return "Activity";
        } else if (length > 1) {
            return "Activities";
        }
    }

    getIconPath(asset: Asset) {
        if (asset) {
            let assetType: string = asset.assetTypeId;
            if (asset.assetActivityTypeId) {
                assetType = asset.assetActivityTypeId;
            }
            return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                assetType
            );
        }
    }

    formatMaxAttempts(maxAttempts: number) {
        if (maxAttempts === -1) {
            return "unlimited tries";
        } else if (maxAttempts === 1) {
            return maxAttempts + " try";
        } else {
            return maxAttempts + " tries";
        }
    }

    trackByFn(index: number) {
        return index;
    }

    // Get Custom Activity Response Type
    getCustomActivityResponseType(responseType: string) {
        if (responseType) {
            return this.assignmentHelper.formatResponseType(responseType);
        }
    }

    showMaxAttempts(data: Assignment) {
        return data.asset?.assetTypeId === assetTypeIds.EBOOK_ACT;
    }

    multilevel(asset: Asset): boolean {
        const levelName = this.getLevelName(asset);
        return !!levelName && levelName.includes(",");
    }

    // Retrieve proper Level name
    getLevelName(asset: Asset) {
        const edition = this.data.allEditions?.find(
            (e: Edition) => e.editionId === this.data.editionId
        );
        if (asset.levelIds && this.data.subject) {
            return this.assetAssociationsHelper.getAssetLevelNames(
                asset,
                this.data.subject,
                edition,
                this.data.allEditions
            );
        } else {
            return "---";
        }
    }

    getCompletedAt(activity: Asset, assignment: Assignment) {
        const activityFound = assignment.compositeTaskAssignments.find(
            (c) => c.assetId === activity.assetId
        );
        if (activityFound) {
            return activityFound.completedAt;
        }
    }

    close(): void {
        this.dialogRef.close(1);
    }

    getScorePossible(assignment: Assignment) {
        if (assignment) {
            return this.getGradesHelper.getScorePossible(assignment);
        }
    }

    // get the asset type name for the SVG icon path
    getAssignableTypeForIcon(tile: Assignment | CompositeTaskAssignment) {
        if (tile && tile.asset) {
            if (tile.asset.assetActivityTypeId) {
                return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                    tile.asset.assetActivityTypeId
                );
            }

            return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                tile.asset.assetTypeId
            );
        }
    }

    // Get the capitalized asset type name
    getCapAssignableTypeName(tile: Assignment | CompositeTaskAssignment) {
        if (tile && tile.asset) {
            if (tile.asset.assetActivityTypeId) {
                return this.assetTypeHelper.getCapTypeName(
                    tile.asset.assetActivityTypeId
                );
            }

            return this.assetTypeHelper.getCapTypeName(tile.asset.assetTypeId);
        }
    }

    launchButton() {
        this.dialogRef.close("launch-assignment");
    }

    // Get correct Due Date Status
    getDueDateStatus(
        assignment: Assignment | CompositeTaskAssignment | undefined
    ) {
        if (assignment && !assignment.completedAt) {
            return this.dueDateStatusHelper.getDueDateStatus(assignment);
        }
    }

    getCheckCircleColor(tile: Assignment | CompositeTaskAssignment) {
        if (
            tile?.dueStatus === "submittedLate" ||
            this.getDueDateStatus(tile) === "overdueButEvaluated"
        ) {
            return "overdue";
        } else if (tile?.status === "evaluated") {
            return "evaluated";
        } else {
            return "pending";
        }
    }

    getGrades(assignment: Assignment) {
        return this.getGradesHelper.getGrades(assignment);
    }

    isMathCourse() {
        return this.languageHelper.ifMathLanguage(
            this.data?.assignment?.asset?.subjectId
        );
    }
}
